'use client'
import * as React from 'react'
import { ArrowRight, Sparkles, Check, AlertTriangle, Info, Copy, CheckCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { gradeProductDescription, type GraderResult, type DimensionScore } from '@/lib/productDescriptionScore'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'

const SAMPLE = `Introducing our premium quality merino wool beanie, the seamless, must-have accessory for the modern wardrobe. Crafted from 100% ethically sourced merino wool, this cutting-edge beanie is one-of-a-kind and perfect for everyone. It weighs just 60g and comes in a one-size-fits-all design that elevates any outfit whether you're commuting or hitting the slopes, keeping you warm all day without the itch of cheaper wool, and it's machine washable so you can wear it again and again. Loved by thousands of customers and backed by our 30-day money-back guarantee.`

function bandTone(band: GraderResult['band']) {
  switch (band) {
    case 'strong': return { text: 'text-emerald-700 dark:text-emerald-300', ring: 'border-emerald-500/40', bar: 'bg-emerald-500', label: 'Strong' }
    case 'good':   return { text: 'text-sky-700 dark:text-sky-300',         ring: 'border-sky-500/40',     bar: 'bg-sky-500',     label: 'Good' }
    case 'fair':   return { text: 'text-amber-700 dark:text-amber-300',     ring: 'border-amber-500/40',   bar: 'bg-amber-500',   label: 'Fair' }
    default:       return { text: 'text-rose-700 dark:text-rose-300',       ring: 'border-rose-500/40',    bar: 'bg-rose-500',    label: 'Weak' }
  }
}

function dimBar(score: number): string {
  if (score >= 75) return 'bg-emerald-500'
  if (score >= 45) return 'bg-amber-500'
  return 'bg-rose-500'
}

function DimensionRow({ d }: { d: DimensionScore }) {
  return (
    <li className="rounded-xl border bg-background p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-medium leading-snug text-foreground">{d.label}</p>
        <span className="font-display text-sm font-bold tabular-nums text-foreground">{d.score}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-foreground/10">
        <div className={`h-full rounded-full ${dimBar(d.score)}`} style={{ width: `${Math.max(3, d.score)}%` }} aria-hidden />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{d.verdict}</p>
    </li>
  )
}

function issueIcon(sev: 'high' | 'medium' | 'low') {
  if (sev === 'high') return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
  if (sev === 'medium') return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
  return <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
}

function rewriteToText(r: GraderResult['rewrite']): string {
  const lines: string[] = []
  lines.push(r.hook, '')
  for (const b of r.bullets) lines.push(`• ${b}`)
  lines.push('', 'Specs:')
  for (const s of r.specs) lines.push(`- ${s}`)
  lines.push('', r.trustLine, '', r.cta)
  return lines.join('\n')
}

export function ProductDescriptionGrader() {
  const [text, setText] = React.useState('')
  const [result, setResult] = React.useState<GraderResult | null>(null)
  const [copied, setCopied] = React.useState(false)

  function onGrade(e: React.FormEvent) {
    e.preventDefault()
    setResult(gradeProductDescription(text))
    setCopied(false)
  }

  function onCopy() {
    if (!result) return
    navigator.clipboard?.writeText(rewriteToText(result.rewrite)).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => {},
    )
  }

  const tone = result ? bandTone(result.band) : null

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={onGrade} className="rounded-2xl border bg-card p-5 sm:p-6">
        <label htmlFor="pd-input" className="text-sm font-medium text-foreground">
          Paste your product description
        </label>
        <textarea
          id="pd-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={7}
          placeholder="Paste the body copy from one product page. The grader runs entirely in your browser, nothing is uploaded."
          className="mt-2 w-full resize-y rounded-xl border bg-background p-3.5 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => { setText(SAMPLE); setResult(null) }}
            className="text-left text-xs font-medium text-primary underline underline-offset-4 hover:text-primary/80"
          >
            Try it with a sample description
          </button>
          <Button type="submit" size="lg" disabled={!text.trim()} className="shrink-0">
            Grade it <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {result && tone && (
        <div className="mt-8 space-y-6">
          {/* Headline score */}
          <div className={`rounded-2xl border-2 ${tone.ring} bg-card p-6 sm:p-8`}>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Description score</p>
                <p className={`mt-1 font-display text-xl font-semibold ${tone.text}`}>{tone.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {result.wordCount} words · {result.sentenceCount} sentence{result.sentenceCount === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex shrink-0 items-baseline gap-1">
                <span className="font-display text-6xl font-bold tabular-nums text-foreground">{result.score}</span>
                <span className="text-2xl text-muted-foreground">/ 100</span>
              </div>
            </div>
            <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-foreground/10">
              <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.max(3, result.score)}%` }} aria-hidden />
            </div>
          </div>

          {/* Per-dimension breakdown */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              The breakdown, by dimension
            </p>
            <ul className="mt-3 grid gap-3 sm:grid-cols-2">
              {result.dimensions.map((d) => (
                <DimensionRow key={d.id} d={d} />
              ))}
            </ul>
          </div>

          {/* Flagged issues */}
          {result.issues.length > 0 && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                What is costing you points
              </p>
              <ul className="mt-3 grid gap-2.5">
                {result.issues.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5 rounded-xl border bg-background p-3.5 text-sm">
                    {issueIcon(it.severity)}
                    <span className="text-foreground/85">{it.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Priority suggestions */}
          {result.suggestions.length > 0 && (
            <div className="rounded-2xl border bg-secondary/30 p-5">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                Fix these first
              </p>
              <ul className="mt-3 space-y-2">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Structured rewrite scaffold */}
          <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/[0.06] to-card p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-display text-lg font-semibold tracking-tight">A cleaner structure to start from</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Your own copy, stripped of filler and reordered into the shape a PDP wants. Bracketed lines are prompts to fill in, this scaffold never invents specs or claims for you.
                  </p>
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={onCopy} className="shrink-0">
                {copied ? <><CheckCheck className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </Button>
            </div>

            <div className="mt-5 space-y-4 rounded-xl border bg-background p-4 sm:p-5">
              <p className="font-display text-base font-semibold leading-snug text-foreground">{result.rewrite.hook}</p>
              <ul className="space-y-1.5">
                {result.rewrite.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[15px] leading-relaxed text-foreground/90">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Specs</p>
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/85">
                  {result.rewrite.specs.map((s, i) => (
                    <li key={i}>· {s}</li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-foreground/75">{result.rewrite.trustLine}</p>
              <p className="text-sm font-medium text-foreground">{result.rewrite.cta}</p>
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              This is a structured improvement scaffold, not a finished rewrite. Idukki&rsquo;s AI takes the same input further: it pulls your real verified reviews and UGC into the page, drafts benefit copy in your brand voice, and binds it to the SKU so AI shopping engines can quote it.
            </p>
          </div>

          {/* Lead magnet */}
          <div className="rounded-2xl border bg-card p-5 sm:p-6">
            <NewsletterCapture
              source="tool:product-description-grader"
              headline="Get the PDP copy checklist + the next teardown"
              caption="One email with the scannable PDP-description checklist this grader is built on, plus new long-form teardowns when they ship. No drip sequence."
              cta="Send it over"
            />
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Want the AI to do the rewrite for real?{' '}
              <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">Start a free Idukki trial</a>.
            </p>
          </div>

          <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-xs text-muted-foreground">
            The grader is heuristic and deterministic: the same description always scores the same. It runs in your browser and weighs the signals that separate a converting, quotable description from a thin one. It is a starting point, not a verdict.
          </div>
        </div>
      )}
    </div>
  )
}
